// TXDOT Statewide Letting Schedule
// Source: https://www.txdot.gov/business/letting-bids/letting-schedule.html
// Last Updated: December 2024

export const TXDOT_LETTING_DATES = {
  2025: [
    { start: '2025-01-07', end: '2025-01-08', label: 'Jan. 7–8, 2025' },
    { start: '2025-02-06', end: '2025-02-07', label: 'Feb. 6–7, 2025' },
    { start: '2025-03-04', end: '2025-03-05', label: 'March 4–5, 2025' },
    { start: '2025-04-03', end: '2025-04-04', label: 'April 3–4, 2025' },
    { start: '2025-05-01', end: '2025-05-02', label: 'May 1–2, 2025' },
    { start: '2025-06-04', end: '2025-06-05', label: 'June 4–5, 2025' },
    { start: '2025-07-01', end: '2025-07-02', label: 'July 1–2, 2025' },
    { start: '2025-08-07', end: '2025-08-08', label: 'Aug. 7–8, 2025' },
    { start: '2025-09-04', end: '2025-09-05', label: 'Sept. 4–5, 2025' },
    { start: '2025-10-08', end: '2025-10-09', label: 'Oct. 8–9, 2025' },
    { start: '2025-11-05', end: '2025-11-06', label: 'Nov. 5–6, 2025' },
    { start: '2025-12-02', end: '2025-12-03', label: 'Dec. 2–3, 2025' },
  ],
  2026: [
    { start: '2026-01-06', end: '2026-01-07', label: 'Jan. 6–7, 2026' },
    { start: '2026-02-04', end: '2026-02-05', label: 'Feb. 4–5, 2026' },
    { start: '2026-03-04', end: '2026-03-05', label: 'March 4–5, 2026' },
    { start: '2026-04-07', end: '2026-04-08', label: 'April 7–8, 2026' },
    { start: '2026-05-06', end: '2026-05-07', label: 'May 6–7, 2026' },
    { start: '2026-06-02', end: '2026-06-03', label: 'June 2–3, 2026' },
    { start: '2026-07-01', end: '2026-07-02', label: 'July 1–2, 2026' },
    { start: '2026-08-05', end: '2026-08-06', label: 'Aug. 5–6, 2026' },
    { start: '2026-09-02', end: '2026-09-03', label: 'Sept. 2–3, 2026' },
    { start: '2026-10-06', end: '2026-10-07', label: 'Oct. 6–7, 2026' },
    { start: '2026-11-03', end: '2026-11-04', label: 'Nov. 3–4, 2026' },
    { start: '2026-12-01', end: '2026-12-02', label: 'Dec. 1–2, 2026' },
  ],
  2027: [
    { start: '2027-01-06', end: '2027-01-07', label: 'Jan. 6–7, 2027' },
    { start: '2027-02-02', end: '2027-02-03', label: 'Feb. 2–3, 2027' },
    { start: '2027-03-03', end: '2027-03-04', label: 'March 3–4, 2027' },
    { start: '2027-04-06', end: '2027-04-07', label: 'April 6–7, 2027' },
    { start: '2027-05-05', end: '2027-05-06', label: 'May 5–6, 2027' },
    { start: '2027-06-01', end: '2027-06-02', label: 'June 1–2, 2027' },
    { start: '2027-07-01', end: '2027-07-02', label: 'July 1–2, 2027' },
    { start: '2027-08-04', end: '2027-08-05', label: 'Aug. 4–5, 2027' },
    { start: '2027-09-01', end: '2027-09-02', label: 'Sept. 1–2, 2027' },
    { start: '2027-10-05', end: '2027-10-06', label: 'Oct. 5–6, 2027' },
    { start: '2027-11-02', end: '2027-11-03', label: 'Nov. 2–3, 2027' },
    { start: '2027-12-01', end: '2027-12-02', label: 'Dec. 1–2, 2027' },
  ]
};

// Get all letting dates as a flat array sorted by date
export const getAllLettingDates = () => {
  const allDates = [];
  Object.entries(TXDOT_LETTING_DATES).forEach(([year, dates]) => {
    dates.forEach(date => {
      allDates.push({
        ...date,
        year: parseInt(year),
        startDate: new Date(date.start),
        endDate: new Date(date.end)
      });
    });
  });
  return allDates.sort((a, b) => a.startDate - b.startDate);
};

// Get the next upcoming letting date from today
export const getNextLettingDate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const allDates = getAllLettingDates();
  
  // Find the next letting that hasn't ended yet
  for (const letting of allDates) {
    if (letting.endDate >= today) {
      return letting;
    }
  }
  
  return null; // No future lettings found
};

// Get days until next letting
export const getDaysUntilNextLetting = () => {
  const next = getNextLettingDate();
  if (!next) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const diffTime = next.startDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

// Check if a letting is happening today
export const isLettingToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const allDates = getAllLettingDates();
  
  return allDates.some(letting => {
    const start = new Date(letting.start);
    const end = new Date(letting.end);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return today >= start && today <= end;
  });
};

// Check if a letting is happening tomorrow
export const isLettingTomorrow = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  const allDates = getAllLettingDates();
  
  return allDates.some(letting => {
    const start = new Date(letting.start);
    start.setHours(0, 0, 0, 0);
    return start.getTime() === tomorrow.getTime();
  });
};

// Get upcoming lettings (next N)
export const getUpcomingLettings = (count = 3) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const allDates = getAllLettingDates();
  const upcoming = allDates.filter(letting => letting.endDate >= today);
  
  return upcoming.slice(0, count);
};

// Format the letting date for display
export const formatLettingDate = (letting) => {
  if (!letting) return '';
  
  const startMonth = letting.startDate.toLocaleDateString('en-US', { month: 'short' });
  const startDay = letting.startDate.getDate();
  const endDay = letting.endDate.getDate();
  const year = letting.startDate.getFullYear();
  
  return `${startMonth} ${startDay}–${endDay}, ${year}`;
};

// Get letting status message
export const getLettingStatus = () => {
  const next = getNextLettingDate();
  if (!next) return { message: 'No upcoming lettings', type: 'info' };
  
  const days = getDaysUntilNextLetting();
  
  if (days < 0) {
    // Letting is in progress
    return { 
      message: `TXDOT Letting IN PROGRESS`, 
      detail: formatLettingDate(next),
      type: 'active',
      letting: next
    };
  } else if (days === 0) {
    return { 
      message: `TXDOT Letting TODAY!`, 
      detail: formatLettingDate(next),
      type: 'today',
      letting: next
    };
  } else if (days === 1) {
    return { 
      message: `TXDOT Letting TOMORROW`, 
      detail: formatLettingDate(next),
      type: 'tomorrow',
      letting: next
    };
  } else if (days <= 7) {
    return { 
      message: `Next TXDOT Letting in ${days} days`, 
      detail: formatLettingDate(next),
      type: 'soon',
      letting: next
    };
  } else if (days <= 14) {
    return { 
      message: `Next TXDOT Letting in ${days} days`, 
      detail: formatLettingDate(next),
      type: 'upcoming',
      letting: next
    };
  } else {
    return { 
      message: `Next TXDOT Letting: ${formatLettingDate(next)}`, 
      detail: `${days} days away`,
      type: 'future',
      letting: next
    };
  }
};
